import React, { useState, useEffect } from "react";
import { Tab, Tabs, Button, Spinner, Card } from "react-bootstrap";
import TulosteetTab from "./TulosteetTab";
import { useNavigate } from "react-router-dom";

import {
  getReportsByKiinteisto,
  getAllTemplates,
  changeReportKiinteisto,
  deleteReport,
  createReportFromTemplateType,
} from "../api/api";

import { format } from "date-fns";
import fi from "date-fns/locale/fi";
import { fetchCoverImageWithCache } from "./utils/fetchCoverImageWithCache";

import Can from "../permissions/Can";
import { PERMISSIONS } from "../permissions/permissions";

import { reportUrl } from "./utils/routeConstructor.js";
import KorttiIdGetter from "./KorttiIdGetter.jsx";
import { ReportStateToggle } from "./utils/ReportStateToggle.jsx";

export default function DokumentitTab({ kiinteisto }) {
  const [dokumentitActiveKey, setDokumentitActiveKey] =
    useState("Omat_dokumentit");

  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // ✅ Modal state
  const [showKorttiModal, setShowKorttiModal] = useState(false);

  // ✅ Which report is being moved
  const [reportToMoveId, setReportToMoveId] = useState(null);

  // Delete confirmation state
  const [reportToDelete, setReportToDelete] = useState(null); // { id, name }
  const [deleteInput, setDeleteInput] = useState('');
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();

  // -----------------------------
  // Fetch templates
  // -----------------------------
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const resp = await getAllTemplates();
        setTemplates(resp.data.filter((t) => t.template));
      } catch (err) {
        console.error("Failed to fetch templates:", err);
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, []);

  // -----------------------------
  // Fetch reports
  // -----------------------------
  const fetchReports = async () => {
    if (!kiinteisto?.id_kiinteisto) return;

    try {
      setLoadingReports(true);
      const [resp, imgUrl] = await Promise.all([
        getReportsByKiinteisto(kiinteisto.id_kiinteisto),
        fetchCoverImageWithCache(kiinteisto.id_kiinteisto).catch(() => null),
      ]);
      setReports(resp.data || []);
      setCoverImageUrl(imgUrl);
    } catch (err) {
      console.error("Failed to fetch reports:", err);
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [kiinteisto]);

  // -----------------------------
  // Navigation
  // -----------------------------
  const handleOpenOrCreateReport = async (templateType) => {
    try {
      const resp = await createReportFromTemplateType(kiinteisto.id_kiinteisto, templateType);
      const newReport = resp.data;
      setReports(prev => [...prev, newReport]);
      navigate(
        reportUrl({
          kiinteistoId: kiinteisto.id_kiinteisto,
          templateName: templateType,
          reportId: newReport.id,
          section: newReport.report_sections?.[0]?.type || "kansisivu",
          sectionId: newReport.report_sections?.[0]?.id || 0,
        })
      );
    } catch (err) {
      console.error("Failed to create report:", err);
    }
  };

  const handleOpenExistingReport = (report) => {
    navigate(
      reportUrl({
        kiinteistoId: kiinteisto.id_kiinteisto,
        templateName: report.type,
        reportId: report.id,
        section: report.report_sections?.[0]?.type || "kansisivu",
        sectionId: report.report_sections?.[0]?.id || 0,
      })
    );
  };

  // -----------------------------
  // Group reports
  // -----------------------------
  const groupReports = () => {
    const grouped = {};

    reports.forEach((r) => {
      const type = r.type || "Muut";
      if (!grouped[type])
        grouped[type] = { ready: [], draft: [], archived: [] };

      const status = (r.status || "draft").toLowerCase();
      if (grouped[type][status]) grouped[type][status].push(r);
    });

    return grouped;
  };

  const groupedReports = groupReports();

  // -----------------------------
  // Modal selection result
  // -----------------------------
  const handleDeleteReport = async () => {
    if (!reportToDelete) return;
    setDeleting(true);
    try {
      await deleteReport(reportToDelete.id);
      setReports(prev => prev.filter(r => r.id !== reportToDelete.id));
      setReportToDelete(null);
      setDeleteInput('');
    } catch (err) {
      console.error('Failed to delete report:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleKorttiSelect = async (newKiinteistoId) => {
    if (!reportToMoveId) return;

    try {
      await changeReportKiinteisto(reportToMoveId, newKiinteistoId);

      // Refresh reports after move
      await fetchReports();
    } catch (err) {
      console.error("Failed to move report:", err);
    } finally {
      setShowKorttiModal(false);
      setReportToMoveId(null);
    }
  };

  return (
    <div className="container-fluid px-3 px-md-4 mb-4">
      <div className="mx-auto" style={{ maxWidth: "960px" }}>
        {/* Tabs */}
        <div className="d-flex justify-content-center mt-1 mb-1">
          <Tabs
            activeKey={dokumentitActiveKey}
            onSelect={(k) => setDokumentitActiveKey(k)}
            id="taloyhtiokortti-tabs"
            className="mb-0 custom-tabs2"
            variant="pills"
          >
            <Tab
              eventKey="Omat_dokumentit"
              title={<span className="text-white">Omat dokumentit</span>}
            />

            <Tab
              eventKey="Hae_tulosteita"
              title={<span className="text-white">Hae tulosteita</span>}
            />
          </Tabs>
        </div>

        {/* Content */}
        <div className="mt-1">
          {dokumentitActiveKey === "Omat_dokumentit" && (
            <div className="mt-3">

              {/* New report row */}
              <div className="d-flex align-items-center gap-2 mb-4">
                <select
                  className="form-select form-select-sm"
                  style={{ maxWidth: 260 }}
                  defaultValue=""
                  onChange={e => { if (e.target.value) { handleOpenOrCreateReport(e.target.value); e.target.value = ''; } }}
                >
                  <option value="" disabled>Luo uusi raportti…</option>
                  {templates.map(tpl => (
                    <option key={tpl.type} value={tpl.type}>{tpl.name}</option>
                  ))}
                </select>
                {loadingTemplates && <Spinner animation="border" size="sm" variant="secondary" />}
              </div>

              {/* Report list */}
              {loadingReports ? (
                <div className="text-center my-5">
                  <Spinner animation="border" variant="success" />
                </div>
              ) : reports.length === 0 ? (
                <p className="text-muted">Ei raportteja saatavilla tälle kiinteistölle.</p>
              ) : (
                <div className="d-flex flex-column gap-4">
                  {Object.entries(groupedReports).map(([type, statuses]) => {
                    const items = [...(statuses.ready || []), ...(statuses.draft || []), ...(statuses.archived || [])];
                    if (!items.length) return null;
                    const typeName = templates.find(t => t.type === type)?.name || type;
                    const sortedItems = [...items].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
                    return (
                      <div key={type}>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className="fw-semibold text-secondary" style={{ fontSize: '0.78rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                            {typeName}
                          </span>
                          <div style={{ flex: 1, height: 1, backgroundColor: '#dee2e6' }} />
                          <span className="text-muted" style={{ fontSize: '0.75rem' }}>{items.length} kpl</span>
                        </div>

                        <div className="border rounded overflow-hidden">
                          {sortedItems.map((r, i) => {
                            const msPerDay = 24 * 60 * 60 * 1000;
                            const daysSince = r.field_date ? Math.floor((Date.now() - new Date(r.field_date).getTime()) / msPerDay) : null;
                            const daysLeft = daysSince !== null ? 30 - daysSince : null;
                            const fieldColor = daysLeft === null ? '#6c757d'
                              : daysLeft > 14 ? '#198754'
                              : daysLeft > 0  ? '#dc3545'
                              : '#6c757d';

                            const stateIcon = r.state === 'ready'
                              ? 'bi-file-earmark-check-fill text-success'
                              : r.state === 'archived'
                              ? 'bi-archive text-secondary'
                              : 'bi-file-earmark-text text-warning';

                            return (
                              <div
                                key={r.id}
                                className="px-2 px-sm-3 py-2"
                                style={{
                                  borderTop: i > 0 ? '1px solid #e9ecef' : 'none',
                                  backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa',
                                }}
                              >
                                {/* Name as full-width header */}
                                <div className="d-flex align-items-baseline gap-2 mb-1">
                                  {items.length > 1 && (
                                    <span className="flex-shrink-0" style={{ fontSize: '0.72rem', fontWeight: 700, background: '#d1e7dd', color: '#0a5236', borderRadius: 5, padding: '1px 7px', letterSpacing: '0.02em' }}>
                                      #{i + 1}
                                    </span>
                                  )}
                                  <div className="fw-semibold" style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.95rem)', lineHeight: 1.3, wordBreak: 'break-word' }}>
                                    {r.name || typeName}
                                  </div>
                                </div>

                                {/* Metadata + actions row */}
                                <div className="d-flex align-items-center gap-2 gap-sm-3">
                                  <div
                                    className="flex-shrink-0 rounded overflow-hidden d-none d-sm-flex align-items-center justify-content-center"
                                    style={{
                                      width: 56, height: 40,
                                      backgroundImage: coverImageUrl ? `url(${coverImageUrl})` : 'none',
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                      backgroundColor: '#e9ecef',
                                    }}
                                  >
                                    {!coverImageUrl && <i className={`bi ${stateIcon}`} style={{ fontSize: '1rem' }} />}
                                  </div>

                                  <div className="flex-grow-1 text-muted" style={{ fontSize: '0.75rem' }}>
                                    Muokattu {r.updated_at ? format(new Date(r.updated_at), 'd.M.yyyy', { locale: fi }) : '–'}
                                  </div>

                                  {r.field_date && (
                                    <div className="d-none d-sm-block flex-shrink-0 text-end" style={{ minWidth: 80 }}>
                                      <div style={{ fontSize: '0.78rem', color: fieldColor, fontWeight: 500 }}>
                                        {format(new Date(r.field_date), 'd.M.yyyy', { locale: fi })}
                                      </div>
                                      {daysLeft !== null && daysLeft > 0 && (
                                        <div style={{ fontSize: '0.68rem', color: fieldColor }}>{daysLeft} pv jäljellä</div>
                                      )}
                                    </div>
                                  )}

                                  <div className="flex-shrink-0" onClick={e => e.stopPropagation()}>
                                    <ReportStateToggle
                                      report={r}
                                      onStateChange={(id, newState) =>
                                        setReports(prev => prev.map(rep => rep.id === id ? { ...rep, state: newState } : rep))
                                      }
                                      requireConfirm={true}
                                    />
                                  </div>

                                  <div className="flex-shrink-0 d-flex gap-1">
                                    <Can permission={PERMISSIONS.TALOYHTIOKORTTI.MOVE_REPORT} mode="hide">
                                      <Button
                                        variant="link"
                                        size="sm"
                                        className="p-1 text-muted"
                                        title="Siirrä raportti"
                                        onClick={() => { setReportToMoveId(r.id); setShowKorttiModal(true); }}
                                      >
                                        <i className="bi bi-box-arrow-in-right" />
                                      </Button>
                                    </Can>
                                    <Can permission={PERMISSIONS.REPORTS.EDIT} mode="hide">
                                      <Button
                                        variant="link"
                                        size="sm"
                                        className="p-1 text-danger"
                                        title="Poista raportti"
                                        onClick={() => { setReportToDelete({ id: r.id, name: r.name || typeName }); setDeleteInput(''); }}
                                      >
                                        <i className="bi bi-trash" />
                                      </Button>
                                    </Can>
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      style={{ whiteSpace: 'nowrap' }}
                                      onClick={() => handleOpenExistingReport(r)}
                                    >
                                      <span className="d-none d-sm-inline">Avaa</span>
                                      <i className="bi bi-arrow-right d-sm-none" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tulosteet tab */}
          {dokumentitActiveKey === "Hae_tulosteita" && (
            <Can
              permission={PERMISSIONS.TALOYHTIOKORTTI.USE_HAE_TULOSTEITA}
              mode="disable"
            >
              <TulosteetTab kiinteistotunnus={kiinteisto.kiinteistotunnus} />
            </Can>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {reportToDelete && (() => {
        const matches = deleteInput === reportToDelete.name;
        return (
          <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-danger">
                <div className="modal-header border-danger" style={{ background: '#fff5f5' }}>
                  <h5 className="modal-title text-danger d-flex align-items-center gap-2">
                    <i className="bi bi-exclamation-triangle-fill" />
                    Poista raportti pysyvästi
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="mb-1">
                    Tämä poistaa raportin <strong>{reportToDelete.name}</strong> ja kaikki sen sisältö poistetaan pysyvästi.
                    Toimintoa <strong>ei voi peruuttaa</strong>.
                  </p>
                  <p className="text-muted small mb-3">
                    Vahvista kirjoittamalla raportin nimi tarkalleen:
                  </p>
                  <div className="mb-1 fw-mono text-muted small border rounded px-2 py-1 bg-light" style={{ userSelect: 'all', fontFamily: 'monospace' }}>
                    {reportToDelete.name}
                  </div>
                  <input
                    type="text"
                    className="form-control mt-2"
                    placeholder={reportToDelete.name}
                    value={deleteInput}
                    onChange={e => setDeleteInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && matches && !deleting && handleDeleteReport()}
                    autoFocus
                  />
                  {deleteInput.length > 0 && !matches && (
                    <div className="text-danger small mt-1">Nimi ei täsmää.</div>
                  )}
                </div>
                <div className="modal-footer">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => { setReportToDelete(null); setDeleteInput(''); }}
                    disabled={deleting}
                  >
                    Peruuta
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleDeleteReport}
                    disabled={!matches || deleting}
                  >
                    {deleting ? <><Spinner animation="border" size="sm" className="me-1" />Poistetaan…</> : 'Poista raportti'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ✅ Modal Component */}
      <KorttiIdGetter
        show={showKorttiModal}
        onClose={() => {
          setShowKorttiModal(false);
          setReportToMoveId(null);
        }}
        onSelect={handleKorttiSelect}
      />
    </div>
  );
}
